import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import Notification from '@rio-cloud/rio-uikit/Notification';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';
import { EulaConsent } from '@rio-cloud/rio-user-menu-component';
import isEmpty from 'lodash/fp/isEmpty';
import { lazy, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import { SuspendedWithoutFallback } from '../components/SuspendedWithoutFallback';
import { featureToggles } from '../configuration/setup/featureToggles';
import {
    useActiveAsset,
    useActiveAssetId,
    useActiveChargingStationId,
    useActiveGeofenceId,
    useActivePoiId,
    useActiveSimplePayPoiId,
} from '../features/app/appHooks';
import { DemoFleetBottomSheet } from '../features/bottomSheets/DemoFleetBottomSheet';
import { UXResearchSurveyBottomSheet } from '../features/bottomSheets/UXResearchSurveyBottomSheet';
import { PoiGeofenceDialogContainer } from '../features/dialogs/poiGeofenceDialog/PoiGeofenceDialog';
import FeatureBaseSurveyWrapper from '../features/featureVoting/FeatureBaseSurveyWrapper';
import { FeedbackButton } from '../features/featureVoting/feedbackButton/FeedbackButton';
import { useChargingStationClusterIds, useIsMapLoaded, useSimplePayPoiClusterIds } from '../features/map/mapHooks';
import { useServiceInfoDialogOnStart } from '../features/serviceInfo/useServiceInfoDialogOnStart';
import ClusterSidebarContainer from '../features/sidebar/cluster/ClusterSidebar';
import { getMobileMapSidebarClassNames } from '../features/sidebar/sidebarLayout';
import { MessageHandler } from '../features/widgets/MessageHandler';
import { useInitialData } from '../hooks/useInitialData';
import { useIsMobile } from '../hooks/useIsMobile';
import { useLocationChange } from '../hooks/useLocationChange';
import { useSupportMarkers } from '../hooks/useSupportMarkers';
import { useTimedPageReload } from '../hooks/useTimedPageReload';
import { RouterUpdater } from '../routes/RouteUpdater';
import { MAP_PATH } from '../routes/routes';
import { MainContent } from './MainContent';

const Header = lazy(() => import('../features/header/Header'));
const AssetTreeContainer = lazy(() => import('../features/tree/AssetTree'));
const AssetSidebar = lazy(() => import('../features/sidebar/asset/AssetSidebar'));
const PoiSidebar = lazy(() => import('../features/sidebar/pois/PoiSidebar'));
const GeofenceSidebarContainer = lazy(() => import('../features/sidebar/geofence/GeofenceSidebar'));
const ChargingStationSidebar = lazy(() => import('../features/sidebar/chargingStations/ChargingStationSidebar'));
const SimplePayPoisSidebar = lazy(() => import('../features/sidebar/simplePayPois/SimplePayPoisSidebar'));
const ServiceInfoDialog = lazy(() => import('../features/serviceInfo/ServiceInfoDialog'));
const ShareLinkDialog = lazy(() => import('../features/dialogs/shareLinkDialog/ShareLinkDialog'));
const AssetAdminDialog = lazy(() => import('../features/dialogs/assetAdminDialog/AssetAdminDialog'));
const WidgetDialog = lazy(() => import('../features/dialogs/widgetDialog/WidgetDialog'));

const getOffscreenSidebarClassName = (shouldShow: boolean) => (shouldShow ? '' : 'position-offscreen width-0');

export const AppLayout = () => {
    const intl = useIntl();

    const { data, hasFetchInitialDataFailed, hasFetchGroupsFailed } = useInitialData();

    const activeAssetId = useActiveAssetId();
    const activeAsset = useActiveAsset();
    const activePoiId = useActivePoiId();
    const activeGeofenceId = useActiveGeofenceId();

    const activeChargingStationId = useActiveChargingStationId();
    const chargingStationClusterIds = useChargingStationClusterIds();

    const activeSimplePayPoiId = useActiveSimplePayPoiId();
    const simplePayPoiClusterIds = useSimplePayPoiClusterIds();

    const isMapAvailable = useIsMapLoaded();
    const showMap = useLocation().pathname.startsWith(MAP_PATH);

    const isMobile = useIsMobile();

    useLocationChange();
    useServiceInfoDialogOnStart(showMap);
    useTimedPageReload();
    useSupportMarkers();

    const [hasAssetSidebar, setHasAssetSidebar] = useState(false);
    const [hasPoiSidebar, setHasPoiSidebar] = useState(false);
    const [hasGeofenceSidebar, setHasGeofenceSidebar] = useState(false);
    const [hasChargingStationSidebar, setHasChargingStationSidebar] = useState(false);
    const [hasSimplePayPoisSidebar, setHasSimplePayPoisSidebar] = useState(false);

    // Don't render poi and geofence sidebar if not needed but keep it in the DOM
    // once they have been loaded
    useEffect(() => {
        activePoiId && !hasPoiSidebar && setHasPoiSidebar(true);
    }, [activePoiId, hasPoiSidebar]);
    useEffect(() => {
        activeGeofenceId && !hasGeofenceSidebar && setHasGeofenceSidebar(true);
    }, [activeGeofenceId, hasGeofenceSidebar]);
    useEffect(() => {
        activeAssetId && !hasAssetSidebar && setHasAssetSidebar(true);
    }, [activeAssetId, hasAssetSidebar]);

    useEffect(() => {
        if (!activeChargingStationId && isEmpty(chargingStationClusterIds)) {
            setHasChargingStationSidebar(false);
        } else {
            (activeChargingStationId || chargingStationClusterIds) &&
                !hasChargingStationSidebar &&
                setHasChargingStationSidebar(true);
        }
    }, [activeChargingStationId, chargingStationClusterIds, hasChargingStationSidebar]);

    useEffect(() => {
        // if (!activeSimplePayPoiId && isEmpty(chargingStationClusterIds)) {
        //     setHasSimplePayPoisSidebar(false);
        // } else {
        //     (activeSimplePayPoiId || simplePayPoiClusterIds) &&
        //         !hasSimplePayPoisSidebar &&
        //         setHasSimplePayPoisSidebar(true);
        // }
        activeSimplePayPoiId && !hasSimplePayPoisSidebar && setHasSimplePayPoisSidebar(true);
    }, [activeSimplePayPoiId, simplePayPoiClusterIds, hasSimplePayPoisSidebar]);

    // Note: Workaround to show a message about failed fetch when polling data but still showing the table
    if (data && hasFetchInitialDataFailed) {
        Notification.warning(intl.formatMessage({ id: 'intl-msg:livemonitor.fetch.data.failed' }));
    }

    const mobileClassNames = getMobileMapSidebarClassNames(isMobile);

    return (
        <ApplicationLayout className={'LiveMonitor'}>
            <ApplicationLayout.Header>
                <SuspendedWithoutFallback>
                    <Header />
                    {!featureToggles.isTestEnv && <EulaConsent />}
                </SuspendedWithoutFallback>
            </ApplicationLayout.Header>
            {(showMap || isMapAvailable) && (
                <ApplicationLayout.Sidebar className={`position-relative ${getOffscreenSidebarClassName(showMap)}`}>
                    <SuspendedWithoutFallback>
                        <AssetTreeContainer />
                        {featureToggles.featureVoting && <FeedbackButton />}
                    </SuspendedWithoutFallback>
                </ApplicationLayout.Sidebar>
            )}
            <SuspendedWithoutFallback>
                {hasAssetSidebar && (
                    <ApplicationLayout.Sidebar
                        className={`AssetSidebar right ${mobileClassNames} ${getOffscreenSidebarClassName(!!activeAsset)}`}
                    >
                        <AssetSidebar />
                    </ApplicationLayout.Sidebar>
                )}
                <ApplicationLayout.Sidebar
                    className={`ClusterSidebarContainer ${isMobile ? 'height-50vh position-absolute bottom-0' : 'right'}`}
                >
                    <ClusterSidebarContainer />
                </ApplicationLayout.Sidebar>
                {activePoiId && showMap && isMapAvailable && (
                    <ApplicationLayout.Sidebar className={`PoisSidebar right ${mobileClassNames}`}>
                        <PoiSidebar />
                    </ApplicationLayout.Sidebar>
                )}
                {activeGeofenceId && showMap && isMapAvailable && (
                    <ApplicationLayout.Sidebar
                        className={`GeofenceSidebarContainer right ${mobileClassNames} ${getOffscreenSidebarClassName(!!activeGeofenceId)}`}
                    >
                        <GeofenceSidebarContainer />
                    </ApplicationLayout.Sidebar>
                )}
                {hasChargingStationSidebar && showMap && isMapAvailable && (
                    <ApplicationLayout.Sidebar
                        className={`ChargingStationContainer right ${mobileClassNames} ${getOffscreenSidebarClassName(
                            hasChargingStationSidebar // use flag here for showing cs cluster in sidebar
                        )}`}
                    >
                        <ChargingStationSidebar />
                    </ApplicationLayout.Sidebar>
                )}
                {hasSimplePayPoisSidebar && showMap && isMapAvailable && (
                    <ApplicationLayout.Sidebar
                        className={`SimplePayPoisContainer right ${mobileClassNames} ${getOffscreenSidebarClassName(!!activeSimplePayPoiId)}`}
                    >
                        <SimplePayPoisSidebar />
                    </ApplicationLayout.Sidebar>
                )}
            </SuspendedWithoutFallback>
            <ApplicationLayout.Body
                innerClassName={`responsive ${showMap ? 'padding-0' : ''}`}
                // banner={
                //     <ApplicationLayoutBodyBanner className='bg-warning'>
                //         <FormattedMessage id='intl-msg:livemonitor.banner.warning' />
                //     </ApplicationLayoutBodyBanner>
                // }
            >
                <MessageHandler />
                <NotificationsContainer />
                <SuspendedWithoutFallback>
                    <ShareLinkDialog />
                    <ServiceInfoDialog />
                    <PoiGeofenceDialogContainer />
                    <AssetAdminDialog />
                    <WidgetDialog />
                </SuspendedWithoutFallback>
                <MainContent
                    isMap={showMap}
                    hasMapAlreadyLoaded={isMapAvailable}
                    data={data}
                    hasFetchInitialDataFailed={hasFetchInitialDataFailed}
                    hasFetchGroupsFailed={hasFetchGroupsFailed}
                />
                {/* <DummyBottomSheet /> */}
                {/* <MarketplaceSurveyBottomSheet /> */}
                {/* <GeoResearchBottomSheet /> */}
                {/* <MapUpdateFeedbackBottomSheet /> */}
                {/* <OrderCommunicationSurveyBottomSheet /> */}
                <UXResearchSurveyBottomSheet />
                <DemoFleetBottomSheet />
                <RouterUpdater />
                <FeatureBaseSurveyWrapper />
            </ApplicationLayout.Body>
        </ApplicationLayout>
    );
};
